/**
 * Crear un Estudio Contable (platform-admin). Opcionalmente nombra a su primer
 * administrador por correo. Mapea a POST /firm/admin/firms/.
 */
import { useState } from 'react'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/Button'
import { getApiErrorMessage } from '@/lib/apiClient'
import { useCreateFirm, useLinkFirmClient, type PlatformFirm } from '@/modules/firm/hooks/useFirmAdmin'
import type { AccountingFirm } from '@/modules/firm/hooks/useFirm'
import { UserEmailPicker, TenantPicker, adminInputCls } from './firmAdminShared'

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: (firm: AccountingFirm | PlatformFirm) => void
  toast: { success: (m: string) => void; error: (m: string) => void }
}

export function CreateFirmModal({ open, onClose, onCreated, toast }: Props) {
  const create = useCreateFirm()
  const linkClient = useLinkFirmClient()
  const [name, setName] = useState('')
  const [taxId, setTaxId] = useState('')
  const [billingEmail, setBillingEmail] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [selfManage, setSelfManage] = useState(false)
  const [ownSchema, setOwnSchema] = useState('')
  const [ownName, setOwnName] = useState('')

  const reset = () => {
    setName(''); setTaxId(''); setBillingEmail(''); setAdminEmail('')
    setSelfManage(false); setOwnSchema(''); setOwnName('')
  }

  const pending = create.isPending || linkClient.isPending

  const handleSubmit = async () => {
    if (!name.trim()) return
    try {
      const firm = await create.mutateAsync({
        name: name.trim(),
        tax_id: taxId.trim() || undefined,
        billing_email: billingEmail.trim() || undefined,
        admin_email: adminEmail.trim() || undefined,
      })
      // Autogestión opcional: vincular el tenant propio del estudio como empresa propia.
      if (selfManage && ownSchema) {
        await linkClient.mutateAsync({
          firm_id: firm.id, tenant_schema: ownSchema, tenant_name: ownName, is_own_company: true,
        })
      }
      toast.success(`Estudio "${firm.name}" creado.`)
      reset()
      onClose()
      onCreated?.(firm)
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Crear Estudio Contable"
      size="sm"
      actions={
        <>
          <ModalCancelButton onClick={onClose} />
          <Button onClick={handleSubmit} disabled={!name.trim() || (selfManage && !ownSchema)} isPending={pending} variant="primary" size="lg">
            Crear estudio
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="createfirmmodal-razon-social-del-estudio" className="block text-sm font-medium text-content mb-1">Razón social del estudio *</label>
          <input id="createfirmmodal-razon-social-del-estudio" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Asesoría y Gestión Integral SPA" className={adminInputCls} autoFocus />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="createfirmmodal-rut" className="block text-sm font-medium text-content mb-1">RUT</label>
            <input id="createfirmmodal-rut" value={taxId} onChange={(e) => setTaxId(e.target.value)}
              placeholder="77496434-7" className={adminInputCls} />
          </div>
          <div>
            <label htmlFor="createfirmmodal-correo-de-facturacion" className="block text-sm font-medium text-content mb-1">Correo de facturación</label>
            <input id="createfirmmodal-correo-de-facturacion" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)}
              placeholder="pagos@estudio.cl" className={adminInputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-content mb-1">Administrador del estudio</label>
          <UserEmailPicker value={adminEmail} onChange={setAdminEmail} placeholder="Buscar usuario por correo o nombre…" />
          <p className="text-xs text-content-subtle mt-1.5">
            Opcional. Quien quede como administrador verá el portal del estudio tras volver a iniciar sesión.
            Puedes agregarlo después desde el detalle del estudio.
          </p>
        </div>

        {/* Autogestión opcional */}
        <div className="pt-1 border-t border-divider">
          <label className="flex items-start gap-2 text-sm text-content cursor-pointer pt-3">
            <input type="checkbox" checked={selfManage} onChange={(e) => setSelfManage(e.target.checked)}
              className="mt-0.5 rounded border-divider-strong text-primary focus:ring-primary" />
            <span>
              El estudio gestionará su propia contabilidad en Clobi
              <span className="block text-xs text-content-subtle">
                Vincula el tenant operativo del propio estudio como “empresa propia”. Es opcional: un
                estudio puede gestionar solo a sus clientes. Pero si su administrador es un usuario nuevo
                sin acceso a ningún tenant, este tenant le da el acceso para iniciar sesión y ver el portal.
              </span>
            </span>
          </label>
          {selfManage && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-content mb-1">Tenant propio del estudio</label>
              <TenantPicker value={ownSchema} onChange={(s, n) => { setOwnSchema(s); setOwnName(n) }} />
              <p className="text-xs text-content-subtle mt-1.5">
                ¿No existe aún? Créalo primero en la pestaña <strong>Tenants</strong> (con el correo del administrador
                como admin del tenant) y luego selecciónalo aquí.
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
